/**
 * Operations Dashboard Hooks
 *
 * TanStack Query hooks for fetching dashboard metrics.
 */

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  fetchDashboardSummary,
  fetchWorkload,
  fetchTimeline,
  fetchActivity,
} from '../api';

// =============================================================================
// Query Key Factory
// =============================================================================

export const metricsKeys = {
  all: ['operations-metrics'] as const,
  summary: () => [...metricsKeys.all, 'summary'] as const,
  workload: () => [...metricsKeys.all, 'workload'] as const,
  timeline: (days: number) => [...metricsKeys.all, 'timeline', days] as const,
  activity: (limit: number) => [...metricsKeys.all, 'activity', limit] as const,
};

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Fetch dashboard summary statistics
 */
export function useDashboardSummary() {
  return useQuery({
    queryKey: metricsKeys.summary(),
    queryFn: fetchDashboardSummary,
    staleTime: 30_000, // 30 seconds
  });
}

/**
 * Fetch team workload distribution
 */
export function useWorkload() {
  return useQuery({
    queryKey: metricsKeys.workload(),
    queryFn: fetchWorkload,
    staleTime: 30_000, // 30 seconds
  });
}

/**
 * Fetch upcoming deadlines
 * @param days - Number of days to look ahead (default 14)
 */
export function useTimeline(days: number = 14) {
  return useQuery({
    queryKey: metricsKeys.timeline(days),
    queryFn: () => fetchTimeline(days),
    staleTime: 60_000, // 1 minute
  });
}

/**
 * Fetch recent activity
 * @param limit - Number of items to return (default 20)
 */
export function useActivity(limit: number = 20) {
  return useQuery({
    queryKey: metricsKeys.activity(limit),
    queryFn: () => fetchActivity(limit),
    staleTime: 30_000, // 30 seconds
  });
}

// =============================================================================
// Projects Kanban Hooks
// =============================================================================

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  fetchProjectsKanban,
  advanceProjectStage,
  updateProjectPositions,
  updateQuotePriority,
  fetchTransmittersByQuote,
  fetchQuoteDevices,
  confirmQuoteDevices,
} from '../api';
import { projectsKeys } from '@/features/operations-projects/hooks/useProjects';
import { quoteKeys } from '@/features/quotes';
import type { ProjectsKanbanData, KanbanQueryParams, AdvanceStageInput, UpdateProjectPositionsInput } from '../types';
import type { DeviceConfirmInput } from '../api';

export const kanbanKeys = {
  all: ['operations-projects-kanban'] as const,
  board: (params: KanbanQueryParams) => [...kanbanKeys.all, 'board', params] as const,
};

export function useProjectsKanban(params: KanbanQueryParams = {}) {
  return useQuery({
    queryKey: kanbanKeys.board(params),
    queryFn: () => fetchProjectsKanban(params),
    staleTime: 10_000,
    // Filter changes swap the query key; without this the board (and the
    // From/To inputs the user is typing into) unmounts to a spinner on every
    // change. Keep showing the previous board until the filtered one arrives.
    placeholderData: keepPreviousData,
  });
}

export function useAdvanceProjectStage() {
  const queryClient = useQueryClient();
  const kanbanBaseKey = kanbanKeys.all;

  return useMutation({
    mutationFn: (input: AdvanceStageInput) => advanceProjectStage(input),

    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: kanbanBaseKey });
      const previousKanban = queryClient.getQueriesData<ProjectsKanbanData>({ queryKey: kanbanBaseKey });

      queryClient.setQueriesData<ProjectsKanbanData>({ queryKey: kanbanBaseKey }, (old) => {
        if (!old) return old;
        const columns = old.columns.map((col) => ({ ...col, projects: [...col.projects], totals: { ...col.totals } }));

        let movedProject: (typeof columns)[0]['projects'][0] | undefined;
        for (const col of columns) {
          const idx = col.projects.findIndex((p) => p.id === input.quoteId);
          if (idx !== -1) {
            movedProject = col.projects.splice(idx, 1)[0];
            col.totals.projectCount--;
            col.totals.sensorsQty -= movedProject.sensorsQty;
            col.totals.value -= movedProject.amount;
            break;
          }
        }

        if (movedProject) {
          const targetCol = columns.find((c) => c.stage === input.targetStage);
          if (targetCol) {
            movedProject = { ...movedProject, currentStage: input.targetStage };
            targetCol.projects.push(movedProject);
            targetCol.totals.projectCount++;
            targetCol.totals.sensorsQty += movedProject.sensorsQty;
            targetCol.totals.value += movedProject.amount;
          }
        }

        return { ...old, columns };
      });

      return { previousKanban };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousKanban) {
        for (const [queryKey, data] of context.previousKanban) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      toast.error('Failed to advance project stage');
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: kanbanBaseKey });
      queryClient.invalidateQueries({ queryKey: projectsKeys.lists() });
    },
  });
}

/**
 * Set a quote's operations triage priority from the Kanban detail panel.
 * Optimistically recolors the card, then re-syncs board + quote detail.
 */
export function useUpdateQuotePriority() {
  const queryClient = useQueryClient();
  const kanbanBaseKey = kanbanKeys.all;

  return useMutation({
    mutationFn: ({ quoteId, priority }: { quoteId: string; priority: 'urgent' | 'normal' | 'low' }) =>
      updateQuotePriority(quoteId, priority),

    onMutate: async ({ quoteId, priority }) => {
      await queryClient.cancelQueries({ queryKey: kanbanBaseKey });
      const previousKanban = queryClient.getQueriesData<ProjectsKanbanData>({ queryKey: kanbanBaseKey });

      queryClient.setQueriesData<ProjectsKanbanData>({ queryKey: kanbanBaseKey }, (old) => {
        if (!old) return old;
        return {
          ...old,
          columns: old.columns.map((col) => ({
            ...col,
            projects: col.projects.map((p) =>
              (p.quoteId || p.id) === quoteId ? { ...p, priority } : p),
          })),
        };
      });

      return { previousKanban };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousKanban) {
        for (const [queryKey, data] of context.previousKanban) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      toast.error('Failed to update quote priority');
    },

    onSettled: (_data, _error, { quoteId }) => {
      queryClient.invalidateQueries({ queryKey: kanbanBaseKey });
      queryClient.invalidateQueries({ queryKey: quoteKeys.detail(quoteId) });
    },
  });
}

export function useUpdateProjectPositions() {
  const queryClient = useQueryClient();
  const kanbanBaseKey = kanbanKeys.all;

  return useMutation({
    mutationFn: (input: UpdateProjectPositionsInput) => updateProjectPositions(input),

    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: kanbanBaseKey });
      const previousKanban = queryClient.getQueriesData<ProjectsKanbanData>({ queryKey: kanbanBaseKey });

      queryClient.setQueriesData<ProjectsKanbanData>({ queryKey: kanbanBaseKey }, (old) => {
        if (!old) return old;
        const columns = old.columns.map((col) => ({
          ...col,
          projects: col.projects
            .map((p) => {
              const update = input.updates.find((u) => u.id === p.id);
              return update ? { ...p, position: update.position } : p;
            })
            .sort((a, b) => a.position - b.position),
        }));
        return { ...old, columns };
      });

      return { previousKanban };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousKanban) {
        for (const [queryKey, data] of context.previousKanban) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      toast.error('Failed to update positions');
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: kanbanBaseKey });
    },
  });
}

// =============================================================================
// Shipping MAC-validation window (C2b)
// =============================================================================

export const shippingDeviceKeys = {
  transmittersByQuote: (quoteNumber: string) => ['operations-shipping-transmitters', quoteNumber] as const,
  quoteDevices: (quoteId: string) => ['operations-quote-devices', quoteId] as const,
};

/**
 * C1 pre-fill: flashed transmitters (dev_id = MAC) for a quote, keyed by quote number.
 * Sourced from `transmitter.quote_number`, stamped at flash time and never cleared by the
 * device coming online — a device absent here was never stamped, not unlinked.
 */
export function useTransmittersByQuote(quoteNumber: string | undefined, enabled: boolean = true) {
  return useQuery({
    queryKey: shippingDeviceKeys.transmittersByQuote(quoteNumber ?? ''),
    queryFn: () => fetchTransmittersByQuote(quoteNumber as string),
    enabled: enabled && !!quoteNumber,
    staleTime: 10_000,
  });
}

/**
 * C2a confirmed store: the durable device set already confirmed for a quote.
 */
export function useQuoteDevices(quoteId: string | undefined, enabled: boolean = true) {
  return useQuery({
    queryKey: shippingDeviceKeys.quoteDevices(quoteId ?? ''),
    queryFn: () => fetchQuoteDevices(quoteId as string),
    enabled: enabled && !!quoteId,
    staleTime: 10_000,
  });
}

/**
 * Confirm (persist) the device set for a quote. Soft-deletes the prior confirmed set
 * server-side and returns the fresh one; re-syncs the quote-devices query on success.
 */
export function useConfirmQuoteDevices() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      quoteId,
      devices,
      confirmedByName,
    }: {
      quoteId: string;
      devices: DeviceConfirmInput[];
      confirmedByName?: string;
    }) => confirmQuoteDevices(quoteId, devices, confirmedByName),

    onSuccess: (_data, { quoteId }) => {
      queryClient.invalidateQueries({ queryKey: shippingDeviceKeys.quoteDevices(quoteId) });
    },

    onError: () => {
      toast.error('Failed to confirm devices');
    },
  });
}
